import { computed, Injectable, signal, Signal, WritableSignal } from '@angular/core'

interface PageTimer {
    seconds: WritableSignal<number>
    intervalId: any // null if paused
}

interface TimerState {
    overallSeconds: number
    pageSeconds: number[]
    currentPageIndex: number | null
    meetingId: string
}

@Injectable({
    providedIn: 'root',
})
export class MeetingTimerService {
    private overallSeconds = signal(0)
    readonly overallMeetingTime = this.overallSeconds.asReadonly()

    private overallInterval: any = null
    private pageTimers: PageTimer[] = []
    private currentMeetingId: string | null = null
    private saveInterval: any = null
    /** When true (followers), the timer does not tick locally; it only reflects the lead's broadcast state. */
    private remoteControlled = false

    currentPageIndex = signal<number | null>(null)
    currentPageTime = computed((): number => {
        const currentIndex = this.currentPageIndex()
        if (currentIndex === null) return 0
        return this.pageTimers[currentIndex]?.seconds() ?? 0
    })

    /**
     * Initializes the meeting with a set number of pages.
     * When remoteControlled is true (followers in a live meeting), the timer does not tick or
     * persist locally; it only mirrors snapshots pushed by the lead via applyRemoteSnapshot().
     */
    initializeMeeting(pageCount: number, meetingId?: string, remoteControlled = false): void {
        this.clearMeeting()
        this.remoteControlled = remoteControlled

        this.pageTimers = Array.from({ length: pageCount }, () => ({
            seconds: signal(0),
            intervalId: null,
        }))

        if (remoteControlled) {
            // Followers get their times from the lead's broadcasts; no local ticking/persistence.
            return
        }

        // Try to restore timer state from localStorage
        if (meetingId) {
            this.currentMeetingId = meetingId
            this.restoreTimerState(meetingId, pageCount)
        }

        this.overallInterval = setInterval(() => {
            this.overallSeconds.update(val => val + 1)
        }, 1000)

        // Start periodic saving of timer state
        this.startPeriodicSave()
    }

    /** Snapshot of the current timer state, for the lead to broadcast to followers. */
    getSnapshot(): { overallSeconds: number; pageSeconds: number[]; currentPageIndex: number | null } {
        return {
            overallSeconds: this.overallSeconds(),
            pageSeconds: this.pageTimers.map(timer => timer.seconds()),
            currentPageIndex: this.currentPageIndex(),
        }
    }

    /** Applies a timer snapshot received from the lead (followers only). */
    applyRemoteSnapshot(snapshot: { overallSeconds: number; pageSeconds: number[]; currentPageIndex: number | null }): void {
        if (!snapshot) return

        // Grow the page timer array if the incoming snapshot has more pages than we know about.
        while (this.pageTimers.length < snapshot.pageSeconds.length) {
            this.pageTimers.push({ seconds: signal(0), intervalId: null })
        }

        this.overallSeconds.set(snapshot.overallSeconds)
        snapshot.pageSeconds.forEach((seconds, index) => {
            if (this.pageTimers[index]) {
                this.pageTimers[index].seconds.set(seconds)
            }
        })
        this.currentPageIndex.set(snapshot.currentPageIndex)
    }

    /** Sets the active page, pausing previous one and resuming this one */
    setActivePage(index: number): void {
        if (this.remoteControlled) {
            // Followers just track the index; page times arrive via applyRemoteSnapshot().
            this.currentPageIndex.set(index)
            return
        }

        const prevIndex = this.currentPageIndex()
        if (prevIndex !== null && this.pageTimers[prevIndex]) {
            this.pausePageTimer(prevIndex)
        }

        this.currentPageIndex.set(index)
        this.resumePageTimer(index)

        // Save state immediately when page changes
        this.saveTimerState()
    }

    private resumePageTimer(index: number): void {
        const timer = this.pageTimers[index]
        if (timer && !timer.intervalId) {
            timer.intervalId = setInterval(() => {
                timer.seconds.update(val => val + 1)
            }, 1000)
        }
    }

    private pausePageTimer(index: number): void {
        const timer = this.pageTimers[index]
        if (timer && timer.intervalId) {
            clearInterval(timer.intervalId)
            timer.intervalId = null
        }
    }

    /** Takes over local ticking (used when this browser becomes the meeting lead). */
    assumeLocalControl(meetingId?: string): void {
        this.remoteControlled = false
        if (meetingId) this.currentMeetingId = meetingId

        if (!this.overallInterval) {
            this.overallInterval = setInterval(() => {
                this.overallSeconds.update(val => val + 1)
            }, 1000)
        }

        const currentIndex = this.currentPageIndex()
        if (currentIndex !== null) {
            this.resumePageTimer(currentIndex)
        }

        this.startPeriodicSave()
    }

    /** Stops local ticking and reverts to mirroring the lead (used when leadership is lost). */
    relinquishLocalControl(): void {
        this.remoteControlled = true
        this.stopOverallTimer()
        this.stopAllPageTimers()
        this.stopPeriodicSave()
    }

    stopOverallTimer(): void {
        if (this.overallInterval) {
            clearInterval(this.overallInterval)
            this.overallInterval = null
        }
    }

    stopAllPageTimers(): void {
        this.pageTimers.forEach(timer => {
            if (timer.intervalId) {
                clearInterval(timer.intervalId)
                timer.intervalId = null
            }
        })
    }

    /** Fully resets everything */
    clearMeeting(): void {
        this.stopOverallTimer()
        this.stopAllPageTimers()
        this.stopPeriodicSave()
        this.overallSeconds.set(0)
        this.pageTimers = []
        this.currentPageIndex.set(null)
        this.currentMeetingId = null
        this.remoteControlled = false
    }

    /** Gets the current time signal for a specific page */
    getPageTime(index: number): Signal<number> | null {
        return this.pageTimers[index]?.seconds ?? null
    }

    getActivePageIndex(): Signal<number | null> {
        return this.currentPageIndex.asReadonly()
    }

    /** Saves the current timer state to localStorage */
    private saveTimerState(): void {
        if (!this.currentMeetingId) return

        const timerState: TimerState = {
            overallSeconds: this.overallSeconds(),
            pageSeconds: this.pageTimers.map(timer => timer.seconds()),
            currentPageIndex: this.currentPageIndex(),
            meetingId: this.currentMeetingId,
        }

        localStorage.setItem(`meetingTimer_${this.currentMeetingId}`, JSON.stringify(timerState))
    }

    /** Restores timer state from localStorage */
    private restoreTimerState(meetingId: string, pageCount: number): void {
        const savedState = localStorage.getItem(`meetingTimer_${meetingId}`)
        if (!savedState) return

        try {
            const timerState: TimerState = JSON.parse(savedState)

            // Only restore if the meeting ID matches and page count is the same
            if (timerState.meetingId === meetingId && timerState.pageSeconds.length === pageCount) {
                this.overallSeconds.set(timerState.overallSeconds)

                // Restore page times
                timerState.pageSeconds.forEach((seconds, index) => {
                    if (this.pageTimers[index]) {
                        this.pageTimers[index].seconds.set(seconds)
                    }
                })

                // Restore current page index
                if (timerState.currentPageIndex !== null && timerState.currentPageIndex < pageCount) {
                    this.currentPageIndex.set(timerState.currentPageIndex)
                }
            }
        } catch (error) {
            console.warn('Failed to restore timer state:', error)
        }
    }

    /** Starts periodic saving of timer state */
    private startPeriodicSave(): void {
        if (this.saveInterval) {
            clearInterval(this.saveInterval)
        }

        // Save every 5 seconds
        this.saveInterval = setInterval(() => {
            this.saveTimerState()
        }, 5000)
    }

    /** Stops periodic saving */
    private stopPeriodicSave(): void {
        if (this.saveInterval) {
            clearInterval(this.saveInterval)
            this.saveInterval = null
        }
    }

    /** Clears timer state from localStorage for a specific meeting */
    clearTimerState(meetingId: string): void {
        localStorage.removeItem(`meetingTimer_${meetingId}`)
    }
}
